import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
} from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { graphqlClient } from "@/lib/graphql-client";
import { ClientError } from "graphql-request";
import {
  extractOperationName,
  isExpectedSessionTimeout,
} from "@/lib/graphql-utils";

/**
 * Custom hook for GraphQL mutations using TanStack Query.
 * Automatically includes authentication headers via graphqlClient.
 *
 * @param mutation - GraphQL mutation string
 * @param options - Additional TanStack Query options
 * @returns Mutation result with mutate, mutateAsync, loading, error states
 *
 * @example
 * const { mutate, isPending, error } = useGraphQLMutation(
 *   `mutation UpdateUser($input: UpdateUserInput!) {
 *     updateUser(input: $input) { id email }
 *   }`
 * );
 *
 * mutate({ input: { email: 'new@example.com' } });
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useGraphQLMutation<TData = any, TVariables = Record<string, unknown>>(
  mutation: string,
  options?: UseMutationOptions<TData, ClientError, TVariables>,
  /** Request timeout in ms. Defaults to browser default (~2-5 min). Use for long-running mutations. */
  timeoutMs?: number,
): UseMutationResult<TData, ClientError, TVariables> {
  return useMutation<TData, ClientError, TVariables>({
    mutationFn: async (variables: TVariables) => {
      try {
        return await graphqlClient.request<TData>({
          document: mutation,
          variables: variables as Record<string, unknown>,
          signal: timeoutMs ? AbortSignal.timeout(timeoutMs) : undefined,
        });
      } catch (error) {
        const operationName = extractOperationName(mutation);
        const clientError = error as ClientError;
        if (!isExpectedSessionTimeout(clientError)) {
          Sentry.captureException(error, {
            tags: { graphql_operation: operationName, kind: "mutation" },
            extra: {
              statusCode: clientError.response?.status,
              message: clientError.message,
            },
          });
        }
        throw error;
      }
    },
    ...options,
  });
}
